import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { ChartsReferenceLine, LineChart, LineSeriesType } from '@mui/x-charts';
import { addDays, addHours, subDays } from 'date-fns';
import { useGetOne, useTranslate } from 'react-admin';

import {
  B2B_CAMPAIGN_GRACE_PERIOD_HOURS,
  B2B_CAMPAIGN_REMINDER_DAYS_BEFORE_END,
} from '@Common/constants/b2b/campaigns';
import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';

const REMINDER_COLOR = '#ff9800';

type CompaignTimelineProps = {
  record?: Pick<GetB2bCampaignOneResponse, 'start_date' | 'end_date' | 'is_emails_enabled'>;
  id?: number;
  resource?: string;
};

export function CampaignTimeline({ id, resource, record }: CompaignTimelineProps) {
  const translate = useTranslate();
  const { data } = useGetOne<GetB2bCampaignOneResponse>(
    `${resource}`,
    { id: id ?? -1 },
    {
      enabled: !G.isNullable(id),
    },
  );

  if (!record && !data) {
    return null;
  }

  const campaignData = record ?? data;
  const { start_date, end_date, is_emails_enabled } = campaignData ?? {
    start_date: null,
    end_date: null,
    is_emails_enabled: true,
  };

  const startDate = start_date ? new Date(start_date) : null;
  const endDate = end_date ? new Date(end_date) : null;

  const seriesConfig: LineSeriesType[] = [
    {
      type: 'line',
      color: '#4caf50',
      label: translate('resources.bo_common.b2b.campaigns.timeline.label.consultation_period'),
      dataKey: 'consultation',
    },
    {
      type: 'line',
      color: '#3f51b5',
      label: translate('resources.bo_common.b2b.campaigns.timeline.label.payment_period'),
      dataKey: 'payment',
    },
    {
      type: 'line',
      id: 'present',
      color: 'rgb(180 180 180 / 61%)',
      dataKey: 'present',
      disableHighlight: true,
      showMark: false,
    },
  ];

  const consultationPeriod = startDate && endDate ? [startDate, endDate] : [];
  const paymentPeriod = endDate
    ? [endDate, addHours(endDate, B2B_CAMPAIGN_GRACE_PERIOD_HOURS).valueOf()]
    : [];
  const reminderDate =
    startDate && endDate
      ? new Date(
          Math.max(
            subDays(endDate, B2B_CAMPAIGN_REMINDER_DAYS_BEFORE_END).valueOf(),
            startDate.valueOf(),
          ),
        )
      : null;
  const currentDatePeriod = [
    subDays(Math.min(startDate?.valueOf() ?? Infinity, Date.now()), 10).valueOf(),
    new Date(),
  ];

  const VALUE = 1; // Position of the line on the Y axis
  const dataset = [
    ...consultationPeriod.map((date) => ({
      date: date.valueOf(),
      consultation: VALUE,
    })),
    ...paymentPeriod.map((date) => ({
      date: date.valueOf(),
      payment: VALUE,
    })),
    ...currentDatePeriod.map((date) => ({
      date: date.valueOf(),
      present: VALUE,
    })),
  ];

  return (
    <Box sx={{ padding: 2 }}>
      <LineChart
        sx={{
          '& .MuiLineElement-root': {
            strokeWidth: 9,
          },
          '& .MuiLineElement-series-present': {
            strokeWidth: 15,
          },
        }}
        series={seriesConfig.map((seriesItem) => ({
          ...seriesItem,
          showMark: false,
        }))}
        dataset={dataset}
        xAxis={[
          {
            scaleType: 'utc',
            dataKey: 'date',
            tickMinStep: 24 * 60 * 60 * 1000,
            tickMaxStep: 24 * 60 * 60 * 1000,
            min: startDate ? subDays(startDate, 1).valueOf() : undefined,
            max: endDate ? addDays(endDate, 3).valueOf() : undefined,
            valueFormatter: (value: number) =>
              new Date(value).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
              }), // DD/MM
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: 2,
          },
        ]}
        grid={{ vertical: true }}
        tooltip={{ trigger: 'none' }}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
            padding: 0,
          },
        }}
        leftAxis={null}
        height={110}
        margin={{ top: 10, bottom: 60 }}>
        {reminderDate && is_emails_enabled !== false && (
          <ChartsReferenceLine
            x={reminderDate.valueOf()}
            label={translate('resources.bo_common.b2b.campaigns.timeline.label.reminder_date')}
            labelAlign="start"
            lineStyle={{ stroke: REMINDER_COLOR, strokeWidth: 2, strokeDasharray: '4 3' }}
            labelStyle={{ fill: REMINDER_COLOR, fontSize: 11, fontWeight: 600 }}
            spacing={{ x: 4, y: 0 }}
          />
        )}
      </LineChart>
    </Box>
  );
}
